import { union } from 'lodash-es';
import { getTransactionTimeMs } from './data-model/pouch';

/**
 * True when `key` is one of PouchDB's own fields (`_id`, `_rev`, `_conflicts`, ...)
 * or one of this app's `db__` bookkeeping fields, rather than part of the
 * document's content.
 *
 * Every function in this file that skips, preserves or compares "the PouchDB
 * keys" uses this one test. They must agree: if one of them drew the line in a
 * different place, a document could be reported as changed while the rest of
 * the app considers it unchanged. Change what counts as internal here, never at
 * a call site.
 */
export function isPouchInternalKey(key: string): boolean {
  return key.startsWith('_') || key.startsWith('db__');
}

/**
 * True for `undefined` and `false`. Content comparison treats an absent key, a
 * key set to `undefined` and a key set to `false` as the same value.
 * {@link deepCompareEQ} and {@link bodyFingerprint} both apply this rule, and
 * must keep applying the same one.
 */
export function isAbsentLikeValue(value: unknown): boolean {
  return value === undefined || value === false;
}

// NOTE: Perhaps this function could have been imported from an off-the-shelf package. However, we need to make sure it is in sync
// with deepCompareEQ and deepNormalize below. Those have to be custom, since they are skipping pouchdb keys. We must
// _not_ skip the pouchdb keys. Without pouchdb keys the object cannot be saved.
// `ignoreMap` mirrors deepCompareEQ's parameter of the same name: an object whose true fields name keys to skip.
// opts is passed down the recursion, so ignored keys are dropped at every depth. Used by intake-form snapshotting
// to avoid self-nesting the maxEffortPlanIntakeFormState key into its own snapshot (#3405).
export function deepClone<T>(source: T, opts?: { skipPouch?: true; ignoreMap?: any }): T {
  if (source == null) {
    return null;
  }
  console.assert(typeof source === 'object', `Attempt to clone a non-object value: "${source.toString()}"`);
  if (typeof source !== 'object') {
    return source;
  }
  let skipPouch = opts && opts.skipPouch;
  let ignoreMap = opts && opts.ignoreMap;

  let result = Array.isArray(source) ? [] : {};
  let keys = Object.keys(source);
  for (let k of keys) {
    // skip pouchdb/couchdb-specific keys (if enabled!)
    if (skipPouch && isPouchInternalKey(k)) {
      continue;
    }
    // skip any explicitly ignored keys (#3405)
    if (ignoreMap && ignoreMap[k]) {
      continue;
    }
    let v = source[k];
    // Don't copy member if it is undefined because sending undefined members over
    // the network could transform them to null value which can cause unpredicted 
    // behaviors and errors.
    if (v === undefined) {
      continue;
    }
    if (typeof v == 'object') {
      result[k] = deepClone<T>(v, opts);
    } else if (typeof v === 'number') {
      // don't copy invalid numbers as that breaks Firebase cloud functions marshalling
      if (isFinite(v)) {
        result[k] = v;
      }
    } else {
      result[k] = v;
    }
  }
  return result as T;
}
/**
 * Replaces everything in `destination` with everything in `source`, in place, so
 * that every existing reference to `destination` sees the new data. Unlike
 * {@link deepOverwriteCouchSafe}, this copies `_id`, `_rev` and `db__*` too — use
 * it only when you really do want the destination to become a copy of the source.
 *
 * Three things stop it wrecking the destination, and each closed a real defect
 * rather than a hypothetical one — the history is in `design-docs/sync-integrity.md`,
 * "A record that reaches the save path with no `_id`":
 * - a missing source is a no-op, and `undefined` counts as missing exactly like
 *   `null` does (the `null`-only check is what cost a real user their edit);
 * - a source that is the SAME object as the destination is a no-op too, because
 *   emptying one empties the other (this is what `deepOverwriteCouchSafe` has
 *   always guarded and this function did not);
 * - the source's key list is read BEFORE anything is deleted, so a source that
 *   cannot be read leaves the destination whole.
 */
export function deepOverwrite<T>(destination: T, source: T): void {
  if (source == null || destination == null || source === destination) {
    return;
  }
  // read the source's keys first — nothing below may run if this throws
  let keys = Object.keys(source);
  let destKeys = Object.keys(destination);
  for (let k of destKeys) {
    delete destination[k];
  }
  for (let k of keys) {
    let v = source[k];
    if (typeof v == 'object') {
      destination[k] = deepClone(v); // note that we "recurse" into _another_ function
    } else {
      destination[k] = v;
    }
  }
  return;
}

/**
 * Overwrites destination's data with source's data, preserving destination's PouchDB identity.
 * - Deletes all non-Couch keys from destination
 * - Copies all non-Couch keys from source to destination
 * - Preserves destination's _id, _rev, and db__* keys (never copies from source)
 */
export function deepOverwriteCouchSafe<T>(destination: T, source: T): void {
  if (source == null || destination == null || source === destination) {
    return;
  }
  let destKeys = Object.keys(destination);
  for (let k of destKeys) {
    if (isPouchInternalKey(k)) {
      continue;
    }
    delete destination[k];
  }
  let keys = Object.keys(source);
  for (let k of keys) {
    // Skip Couch-specific fields - preserve destination's _id, _rev, db__trans
    if (isPouchInternalKey(k)) {
      continue;
    }
    let v = source[k];
    if (typeof v == 'object') {
      destination[k] = deepClone(v); // note that we "recurse" into _another_ function
    } else {
      destination[k] = v;
    }
  }
  return;
}

/**
 * Safe editing wrapper for PouchDB documents tracked by the identity map.
 *
 * Problem: database-wrapper.ts maintains ONE canonical JS object per document _id.
 * If you deepClone a document and save the clone, the identity map detects a different
 * object reference and must self-heal (causing Sentry noise and potential _rev conflicts).
 *
 * Solution: use CouchDocDraft to get an editable copy. Bind `draft` to the UI.
 * Before saving, call `commitDraft()` to merge changes back to the canonical object
 * (preserving its _id, _rev, and db__* fields), then save the returned canonical.
 *
 * Usage:
 *   const draft = new CouchDocDraft(await db.loadExerciseAsync(id));
 *   this.exercise = draft.draft;  // bind to template
 *   // ... user edits this.exercise ...
 *   await db.saveExerciseAsync(draft.commitDraft(), transaction);
 */
export class CouchDocDraft<T> {
  readonly canonical: T;
  readonly draft: T;

  constructor(canonical: T) {
    this.canonical = canonical;
    this.draft = deepClone(canonical);
  }

  /** Merge draft edits into the canonical object and return it for saving. */
  commitDraft(): T {
    deepOverwriteCouchSafe(this.canonical, this.draft);
    return this.canonical;
  }
}

/** `ignoreMap` is an object whose true fields define names of fields to ignore in comparisons.
 * E.g. if it is `{ foo: true }` then all fields of name `foo` will be ignored. Note that specifiying any non-truthy value is a no-op. */
export function deepCompareEQ<T extends Object>(target: T, source: T, ignoreMap?: any): boolean {
  if (source == null || target == null) {
    return target == source;
  }

  // for all keys in any of the two
  let keys = union(Object.keys(source), Object.keys(target));
  for (let k of keys) {
    // skip pouchdb/couchdb-specific keys, or any explicitly ignored keys
    if (isPouchInternalKey(k) || (ignoreMap && ignoreMap[k])) {
      continue;
    }

    // get the two values
    let vs = isAbsentLikeValue(source[k]) ? false : source[k];
    let vt = isAbsentLikeValue(target[k]) ? false : target[k];

    // if the values are of different type
    if (typeof vt !== typeof vs) {
      // the objects are different
      return false;
    }

    // if the value is object
    if (typeof vs === 'object') {
      // recurse
      if (!deepCompareEQ(target[k], vs, ignoreMap)) {
        return false;
      }
      // otherwise
    } else {
      // check that values are same
      if (vt !== vs) {
        return false;
      }
    }
  }
  return true;
}

/**
 * The most members {@link bodyFingerprint} will look at before it gives up.
 *
 * This is the bound that actually protects the app, and a depth limit is not a
 * substitute for it. The walk visits every key at every level, so its cost is
 * the number of MEMBERS, not the number of levels: an object that leads back to
 * itself through two different keys reaches depth 64 having visited 2^64 of
 * them. A depth limit alone therefore stops the obvious cycle — an object whose
 * one key points at itself — and lets the branching one hang the app on the read
 * path.
 *
 * Set far above any document this app stores, so a real one is never cut short.
 * Stopping early is not a correctness problem in any case: the fingerprint then
 * describes less of the document, so a difference beyond the cut-off is not
 * noticed. That loses a report; it never invents one.
 */
const FINGERPRINT_MAX_MEMBERS = 50000;

/**
 * How deep {@link bodyFingerprint} will walk. A PouchDB document is plain JSON
 * and nothing this app stores comes close, so reaching this means the object is
 * not what we think it is. Kept alongside the member budget above because it
 * stops a deep cycle immediately rather than after 50000 members of work.
 */
const FINGERPRINT_MAX_DEPTH = 64;

/** Starting value and multiplier of the FNV-1a hash, which are fixed by that algorithm. */
const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

/** Mix one piece of text into a running FNV-1a hash. */
function mixIntoHash(hash: number, text: string): number {
  let h = hash;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, FNV_PRIME);
  }
  return h >>> 0;
}

/**
 * Build a short string that stands in for a document's content, so that two
 * versions of the same document can be told apart later WITHOUT keeping a copy
 * of either one.
 *
 * Why this exists. The app keeps exactly one shared JavaScript object per
 * document, and pages edit that object in place. So the database wrapper cannot
 * tell "this page has changed something and not saved it yet" from "this is
 * exactly what the database gave us", because it never sees the edit happen. It
 * could tell them apart by keeping a copy of the last body the database gave it
 * — that is what `Singleton.lastKnownDatabaseBody` does — but a full copy per
 * document would undo the whole point of holding those documents weakly, which
 * is that they can be collected when nothing is using them. A fingerprint is one
 * short string, so it can be kept for every document without that cost.
 *
 * What it deliberately treats as the SAME content, mirroring
 * {@link deepCompareEQ} at every point:
 * - keys starting with `_` or `db__` are skipped at every depth, because those
 *   are PouchDB's own fields rather than the document's content
 *   ({@link isPouchInternalKey});
 * - a key that is absent, a key set to `undefined`, and a key set to `false` all
 *   count as the same thing ({@link isAbsentLikeValue}).
 *
 * That mirroring is the point rather than a detail. If this function disagreed
 * with `deepCompareEQ` about what "different" means, it would report documents
 * as changed that the rest of the app considers unchanged.
 *
 * Never throws, and never returns an empty string for a real document, so a
 * caller can treat `undefined` as "could not be worked out".
 *
 * Collisions: two genuinely different documents can produce the same
 * fingerprint. That is rare, and it fails in the safe direction — a missed
 * difference means one report is not written, never a wrong one.
 */
export function bodyFingerprint(body: any): string | undefined {
  if (body == null || typeof body !== 'object') {
    return undefined;
  }
  // two hashes with different starting values, joined at the end, so that far
  // fewer different documents land on the same fingerprint than one alone
  let hashA = FNV_OFFSET_BASIS;
  let hashB = FNV_OFFSET_BASIS ^ 0x5bf03635;

  let membersLeft = FINGERPRINT_MAX_MEMBERS;

  const mix = (text: string) => {
    hashA = mixIntoHash(hashA, text);
    hashB = mixIntoHash(hashB, text);
  };

  const walk = (value: any, depth: number): void => {
    // if the budget is spent, stop walking entirely — this is what keeps a
    // cycle reached through more than one key from running for ever
    if (membersLeft <= 0) {
      return;
    }
    if (depth > FINGERPRINT_MAX_DEPTH) {
      mix('!deep');
      return;
    }
    if (value === null) {
      mix('null');
      return;
    }
    if (typeof value !== 'object') {
      // `undefined` and `false` are folded together, and dropped by the caller
      // below, so they never reach here
      mix(typeof value);
      mix(String(value));
      return;
    }
    // arrays and objects are walked the same way, by their keys in a fixed
    // order — for an array those keys are its indexes, so element order is part
    // of the fingerprint
    const keys = Object.keys(value).sort();
    mix('{');
    for (const key of keys) {
      if (isPouchInternalKey(key)) {
        continue;
      }
      const member = value[key];
      // absent, `undefined` and `false` all mean the same thing here, so a key
      // holding one of the latter two is left out exactly as an absent one is
      if (isAbsentLikeValue(member)) {
        continue;
      }
      // every member costs one from the budget, whatever it turns out to be
      membersLeft--;
      if (membersLeft <= 0) {
        mix('!budget');
        return;
      }
      mix(String(key.length));
      mix(key);
      walk(member, depth + 1);
    }
    mix('}');
  };

  try {
    walk(body, 0);
  } catch {
    // a fingerprint is a measurement, so it must never be the thing that breaks
    // a save or a read. Saying "could not be worked out" costs one report.
    return undefined;
  }
  return hashA.toString(16) + '-' + hashB.toString(16);
}

export function deepNormalize<T>(target: T, template: T): void {
  if (target == null) {
    console.assert(template == null);
    return;
  }
  if (template === null) {
    target = null;
    return;
  }
  let keys = Object.keys(template);
  for (let k of keys) {
    let v = template[k];
    if (typeof v == 'object') {
      let t = target[k];
      if (t == null) {
        target[k] = deepClone(v);
        continue;
      } else {
        let arrayT = Array.isArray(t);
        let arrayV = Array.isArray(v);
        if (!arrayT == !arrayV) {
          deepNormalize(t, v);
        } else {
          console.error(`Field ${k} is an ${t} in target, but it is ${v} in template!`);
          target[k] = deepClone(v);
        }
      }
    } else {
      if (target[k] == null) {
        target[k] = v;
      }
    }
  }
}

/** Returns string with all errors, or empty string if no errors. */
export function deepCheckValid<T>(target: T, name: string): string | undefined {
  let result = '';

  if (target == null) {
    return result;
  }

  let keys = Object.keys(target);
  for (let k of keys) {
    let v = target[k];
    if (typeof v === 'object') {
      result += deepCheckValid(v, name + '.' + k);
    } else if (typeof v === 'number') {
      if (!isFinite(v)) {
        result += `Field ${name}.${k} is not finite.\n`;
      }
    }
  }
  return result;
}

export function sequenceEqual<T>(a: T[], b: T[]): boolean {
  if (a === b) {
    return true;
  }

  if (a == null || b == null) {
    return false;
  }

  if (a.length !== b.length) {
    return false;
  }

  // If you don't care about the order of the elements inside
  // the array, you should sort both arrays here.
  // Please note that calling sort on an array will modify that array.
  // you might want to clone your array first.

  for (let i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) {
      return false;
    }
  }

  return true;
}

/**
 * Count total nested members in an object/array for comparison.
 * Used by mergeConflictingDocs to prefer "richer" data.
 */
export function countNestedMembers(obj: any): number {
  if (obj == null) return 0;
  if (typeof obj !== 'object') return 1;

  let count = 0;
  if (Array.isArray(obj)) {
    count = obj.length;
    for (const item of obj) {
      if (typeof item === 'object' && item != null) {
        count += countNestedMembers(item);
      }
    }
  } else {
    const keys = Object.keys(obj).filter(k => !isPouchInternalKey(k));
    count = keys.length;
    for (const k of keys) {
      if (typeof obj[k] === 'object' && obj[k] != null) {
        count += countNestedMembers(obj[k]);
      }
    }
  }
  return count;
}

/**
 * #42: decide whether doc2 was written more recently than doc1, by comparing the two docs'
 * `db__trans` write timestamps (parsed by `getTransactionTimeMs`, which owns the
 * `<iso-timestamp>|<deviceId>` format knowledge alongside `getTransactionDeviceId`). Only a
 * pair of parseable timestamps with doc2 STRICTLY newer returns true — a missing or malformed
 * timestamp on either side returns false, which keeps the merge at its historical doc1-wins
 * tie-breaking.
 */
export function isSecondDocNewer(doc1: any, doc2: any): boolean {
  const t1 = getTransactionTimeMs(doc1);
  const t2 = getTransactionTimeMs(doc2);
  return Number.isFinite(t1) && Number.isFinite(t2) && t2 > t1;
}

/**
 * Merge two conflicting document versions, preferring the one with more data, and — for value
 * ties — the one written LAST (#42).
 * - Arrays: keep the longer one; equal length, keep the newer doc's
 * - Objects: recursively merge, prefer branch with more nested content
 * - Primitives: prefer non-null values; both non-null, prefer the newer doc's (falls back to
 *   doc1's when recency is unknown — see below)
 * - PouchDB keys (_id, _rev, db__*): always preserved from doc1
 *
 * Recency comes from the top-level `db__trans` write timestamps (`<iso>|<deviceId>`, written on
 * every save). It is decided ONCE for the whole document pair and threaded through the recursion,
 * because nested objects carry no `db__trans` of their own. When either timestamp is missing or
 * unparseable the merge behaves exactly as it did before #42: doc1's value wins ties. Before this
 * fix doc1 ALWAYS won ties, so a stale device's push could overwrite newer field values (the #3569
 * cohort data clobbers).
 *
 * @param doc1 Primary document (its _id/_rev will be preserved)
 * @param doc2 Secondary document to merge from
 * @returns Merged document
 */
export function mergeConflictingDocs<T>(doc1: T, doc2: T): T {
  return mergeConflictingDocsInner(doc1, doc2, isSecondDocNewer(doc1, doc2));
}

/**
 * {@link mergeConflictingDocs} with the tie-break decided by the caller instead of by recency:
 * when `preferSecondOnTie` is true, doc2's value wins every tie, otherwise doc1's does. Everything
 * else (the longer array, the non-null value, doc1's `_*`/`db__*` keys) works exactly as there.
 *
 * For a caller that knows better than the write time which version speaks for the user. The
 * conflict resolver uses it for log entries (#6825): an empty placeholder written later must not
 * win ties against the user's own completed entry.
 */
export function mergeConflictingDocsPreferring<T>(doc1: T, doc2: T, preferSecondOnTie: boolean): T {
  return mergeConflictingDocsInner(doc1, doc2, preferSecondOnTie);
}

/** the recursive merge body; `preferSecondOnTie` is the top-level recency decision (#42) */
function mergeConflictingDocsInner<T>(doc1: T, doc2: T, preferSecondOnTie: boolean): T {
  if (doc1 == null) return deepClone(doc2);
  if (doc2 == null) return deepClone(doc1);
  if (typeof doc1 !== 'object' || typeof doc2 !== 'object') {
    // Primitives: prefer non-null, then the newer doc's value (#42)
    return pickPrimitive(doc1, doc2, preferSecondOnTie);
  }

  // Arrays: prefer the longer one; equal length, the newer doc's (#42)
  if (Array.isArray(doc1) && Array.isArray(doc2)) {
    return deepClone(pickByLength(doc1, doc2, preferSecondOnTie)) as T;
  }

  // Objects: merge recursively
  const result: any = {};
  const allKeys = [...new Set([...Object.keys(doc1), ...Object.keys(doc2)])];

  for (const k of allKeys) {
    // Preserve PouchDB keys from doc1
    if (isPouchInternalKey(k)) {
      if (k in (doc1 as any)) {
        result[k] = (doc1 as any)[k];
      }
      continue;
    }

    const val1 = (doc1 as any)[k];
    const val2 = (doc2 as any)[k];

    if (val1 === undefined) {
      result[k] = cloneIfObject(val2);
    } else if (val2 === undefined) {
      result[k] = cloneIfObject(val1);
    } else if (typeof val1 === 'object' && typeof val2 === 'object' && val1 != null && val2 != null) {
      // Both are objects/arrays - check if arrays first
      if (Array.isArray(val1) && Array.isArray(val2)) {
        // Arrays: prefer longer; equal length, the newer doc's (#42)
        result[k] = deepClone(pickByLength(val1, val2, preferSecondOnTie));
      } else if (!Array.isArray(val1) && !Array.isArray(val2)) {
        // Both objects: recursively merge (the top-level recency decision rides along)
        result[k] = mergeConflictingDocsInner(val1, val2, preferSecondOnTie);
      } else {
        // Type mismatch (one array, one object): prefer the one with more content;
        // equal content, the newer doc's (#42)
        result[k] = deepClone(pickByCount(val1, val2, preferSecondOnTie));
      }
    } else {
      // At least one is primitive: prefer non-null, then the newer doc's value (#42)
      result[k] = pickPrimitive(val1, val2, preferSecondOnTie);
    }
  }

  return result as T;
}

/**
 * non-null wins; both non-null (a genuine value tie), the newer doc's value wins (#42).
 *
 * Despite the name this often decides an OBJECT, so the winner is cloned before it is handed back
 * (#4314 R8). The object-vs-object branch above requires both sides to be non-null, so a
 * null-vs-object pair and a primitive-vs-object pair both fall through to here by design. Without
 * the clone the merged document would SHARE a nested object with one of its inputs, and since every
 * caller saves the merged result, a later edit to the saved document would silently mutate the
 * other version too. The sibling pickers (pickByLength, pickByCount) are already deepClone-wrapped
 * at all of their call sites; this one was the gap.
 *
 * The clone is conditional on purpose: deepClone fires a console.assert for a non-object value, and
 * a stray console.assert trips the E2E unexpected-browser-error gate.
 */
function pickPrimitive(val1: any, val2: any, preferSecondOnTie: boolean): any {
  let picked: any;
  if (val1 == null) {
    picked = val2;
  } else if (val2 == null) {
    picked = val1;
  } else {
    picked = preferSecondOnTie ? val2 : val1;
  }
  return cloneIfObject(picked);
}

/**
 * Copy a value if it is an object, and hand back anything else untouched.
 *
 * The merge has to copy whatever it puts in the result, or the merged document
 * would SHARE nested objects with its inputs. But it cannot simply call deepClone
 * on everything: deepClone deliberately fires a `console.assert` for a non-object
 * value, and a stray `console.assert` fails the E2E run through the
 * unexpected-browser-error gate. Every place the merge copies a value therefore
 * goes through here rather than calling deepClone directly.
 *
 * This matters far more since #4314 than it used to. The conflict resolver merges
 * every conflicted document on every device, and "a field only one side has,
 * holding a plain value" is the single most common thing a merge meets — so the
 * two `undefined` branches above used to fire that assert constantly on a real
 * account, where before they were reached only by the occasional save collision.
 */
function cloneIfObject(value: any): any {
  if (value != null && typeof value === 'object') {
    return deepClone(value);
  }
  return value;
}

/** longer array wins (data preservation); equal length, the newer doc's wins (#42) */
function pickByLength(val1: any[], val2: any[], preferSecondOnTie: boolean): any[] {
  if (val1.length !== val2.length) {
    return val1.length > val2.length ? val1 : val2;
  }
  return preferSecondOnTie ? val2 : val1;
}

/** more nested content wins (data preservation); equal count, the newer doc's wins (#42) */
function pickByCount(val1: any, val2: any, preferSecondOnTie: boolean): any {
  const count1 = countNestedMembers(val1);
  const count2 = countNestedMembers(val2);
  if (count1 !== count2) {
    return count1 > count2 ? val1 : val2;
  }
  return preferSecondOnTie ? val2 : val1;
}

/**
 * What `mergeAgainstCommonBase` did, so the caller can report it.
 *
 * The counts are for reporting only. Nothing should change behaviour based on
 * them — the merged document is the answer.
 */
export interface CommonBaseMergeResult<T> {
  /** The merged document. */
  merged: T;
  /**
   * How many fields BOTH sides changed, away from the shared starting point and
   * to different values. These are the only fields the merge had to guess at.
   * Zero means every difference had exactly one author and nothing was guessed.
   */
  guessedFieldCount: number;
  /**
   * How many fields only THIS device changed. These are the fields that a plain
   * overwrite would have thrown away, so this is the count that says how much a
   * caller just saved.
   */
  localOnlyChangeCount: number;
  /**
   * True when no shared starting point was available, so this fell back to
   * {@link mergeConflictingDocs} and the two counts above mean nothing.
   */
  mergedWithoutBase: boolean;
}

/**
 * True for an `_id` value PouchDB will actually accept: a non-empty string.
 *
 * Anything else — absent, `undefined`, an empty string, a non-string — makes
 * PouchDB invent a random uuid instead, which is never what a caller wanted. See
 * `design-docs/sync-integrity.md`, "A record that reaches the save path with no
 * `_id`".
 *
 * This is the one spelling of that rule. `hasUsableDocId` in
 * `client/src/services/database-wrapper-save-recovery.ts` asks the same question
 * of a whole document and answers through this, so the two cannot drift apart —
 * a drift that would be silent, because both sides would go on answering
 * confidently about slightly different sets of values.
 */
export function isUsableDocId(value: unknown): boolean {
  return typeof value === 'string' && value.length > 0;
}

/** True for a value the merge can walk into field by field: an object, but not an array. */
function isWalkableObject(value: any): boolean {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * True for a value that {@link deepClone} throws away: one that is `undefined`,
 * and a number that is not finite (`NaN`, `Infinity`). Both are dropped because
 * they do not survive JSON and break Firebase marshalling.
 *
 * This matters to the merge because the stored starting point is a `deepClone`
 * while the two bodies compared against it are not. Without treating a dropped
 * value and a missing key as the same thing, a field holding `NaN` would differ
 * from the starting point on BOTH sides on every single comparison, for ever —
 * reported each time as a field two devices disagree about, when nobody touched
 * it and the value does not survive being saved either way.
 */
function isDroppedByClone(value: any): boolean {
  return value === undefined || (typeof value === 'number' && !isFinite(value));
}

/**
 * Is this the same value, for the purpose of asking "did this side change it?"
 *
 * `deepCompareEQ` cannot be called on its own for this. It takes the keys of
 * whatever it is given, and a number or a string has no keys — so it answers
 * "equal" for `1` versus `2`. It is only safe once both sides are known to be
 * objects, which is what this wrapper arranges.
 */
function isSameMergeValue(a: any, b: any): boolean {
  if (a === b) return true;
  // two values that both vanish when the document is copied are the same value
  // as far as any later comparison can ever tell
  if (isDroppedByClone(a) && isDroppedByClone(b)) return true;
  if (a == null || b == null) return a == null && b == null;
  if (typeof a !== typeof b) return false;
  if (typeof a === 'object') return deepCompareEQ(a, b);
  return false;
}

/**
 * Merge two versions of one document that are both descended from a KNOWN
 * shared starting point, keeping what each side genuinely changed.
 *
 * # Why this exists, and why {@link mergeConflictingDocs} could not do it
 *
 * `mergeConflictingDocs` compares two documents and nothing else. With only two
 * versions in hand there is no way to tell a value somebody deliberately WROTE
 * from a value they merely CARRIED ALONG out of an older copy — both are just
 * "the value this side has". So it has to break ties with a rule, and its rule
 * is the document's `db__trans` stamp, which records when the document as a
 * whole was last saved. That makes whichever device saved most recently win
 * every field, including the many fields it never touched.
 *
 * That cost a real customer the workout progression of two whole training
 * sessions (#4577). One device held a copy of the training plan that was several
 * days old and saved the whole of it. Every exercise result another device had
 * recorded in the meantime lost its tie to a value that device had simply been
 * carrying since the copy was made, and nothing anywhere reported it.
 *
 * With a shared starting point the question stops being "which is newer?" and
 * becomes "who changed this?", which has an actual answer:
 *
 * - only this device changed the field -> keep this device's value;
 * - only the arriving version changed it -> take the arriving value;
 * - neither changed it -> they agree, so there is nothing to decide;
 * - BOTH changed it, to different values -> this is the only genuine
 *   disagreement. Two objects are walked into so the disagreement is narrowed to
 *   the fields that really clash; anything else falls back to
 *   `mergeConflictingDocs`'s existing rules, and is counted in
 *   `guessedFieldCount` so a caller can report that a guess was made.
 *
 * # Limits, stated plainly so nobody oversells this
 *
 * - It is only as good as the starting point it is given. Pass a `base` that is
 *   not really the shared ancestor of both sides and the answers above are
 *   meaningless. When no starting point is known, pass `null` and this falls
 *   back to `mergeConflictingDocs` and says so in `mergedWithoutBase`.
 * - Arrays are still all-or-nothing. If both sides changed the same array, the
 *   longer one wins whole, exactly as before; elements are never merged.
 * - A field one side DELETED and the other side changed is kept, not deleted.
 *   The merge prefers keeping data over losing it.
 * - PouchDB and app bookkeeping keys (`_id`, `_rev`, `db__*`) are taken from
 *   `local`, matching `mergeConflictingDocs`'s handling of `doc1`, with two
 *   exceptions. A key `local` does not have at all is taken from `incoming`
 *   rather than dropped. And `_id` alone is taken from `incoming` whenever
 *   `local`'s own is not a usable document id — so a present `_id` does NOT
 *   always win, because an `_id` of `undefined` or `''` is worth no more than a
 *   missing one. See the comment at that branch for why either would be harmful.
 *
 * @param base the version both sides started from, or `null` when unknown.
 * @param local the version this device holds. Its identity fields are preserved.
 * @param incoming the version that arrived from somewhere else.
 */
export function mergeAgainstCommonBase<T>(base: T | null | undefined, local: T, incoming: T): CommonBaseMergeResult<T> {
  if (local == null) {
    return { merged: deepClone(incoming), guessedFieldCount: 0, localOnlyChangeCount: 0, mergedWithoutBase: false };
  }
  if (incoming == null) {
    return { merged: deepClone(local), guessedFieldCount: 0, localOnlyChangeCount: 0, mergedWithoutBase: false };
  }
  if (base == null || !isWalkableObject(local) || !isWalkableObject(incoming)) {
    // no shared starting point to reason from (or not a shape that can be walked
    // field by field) — fall back to the two-way merge and say that is what happened
    return {
      merged: mergeConflictingDocs(local, incoming),
      guessedFieldCount: 0,
      localOnlyChangeCount: 0,
      mergedWithoutBase: true,
    };
  }

  const tally = { guessedFieldCount: 0, localOnlyChangeCount: 0 };
  // the tie-break for fields BOTH sides changed is still the #42 recency rule,
  // decided once for the whole document and threaded down, because nested
  // objects carry no `db__trans` of their own
  const merged = mergeAgainstCommonBaseInner(base, local, incoming, isSecondDocNewer(local, incoming), tally);
  return {
    merged: merged as T,
    guessedFieldCount: tally.guessedFieldCount,
    localOnlyChangeCount: tally.localOnlyChangeCount,
    mergedWithoutBase: false,
  };
}

/** running counts for {@link mergeAgainstCommonBase}, filled in as the recursion walks */
interface CommonBaseMergeTally {
  guessedFieldCount: number;
  localOnlyChangeCount: number;
}

/**
 * The recursive body of {@link mergeAgainstCommonBase}. `base` may be null for a
 * nested object that did not exist at the shared starting point — every field
 * below it then counts as changed on both sides, which is the honest answer.
 */
function mergeAgainstCommonBaseInner(
  base: any,
  local: any,
  incoming: any,
  preferIncomingOnTie: boolean,
  tally: CommonBaseMergeTally
): any {
  const result: any = {};
  const allKeys = [...new Set([...Object.keys(local), ...Object.keys(incoming)])];

  for (const k of allKeys) {
    // identity and bookkeeping keys come from this device's copy, so the merged
    // document stays the same document (same rule as mergeConflictingDocs) — but
    // fall back to the arriving side for a key this device does not have.
    //
    // That fallback is load-bearing for `_id`. The identity map can hold a
    // canonical object that has lost its own `_id` (there is a heal for exactly
    // that state, and a design-doc section about it). Dropping the key here would
    // hand the caller a body with no `_id`, and `getOrRegisterCanonicalReference`
    // returns immediately on one of those — so the arriving content would never
    // be applied at all, while the revision it came with still got recorded.
    if (isPouchInternalKey(k)) {
      // an `_id` this device's copy cannot be saved under is worth no more than a
      // missing one, so take the arriving side's — but only when that one is
      // usable, so a body with nothing good on either side comes out as it always
      // did. Scoped to `_id` on purpose: `_rev` and every `db__` key must keep
      // coming from this device's copy whatever they hold.
      if (k === '_id' && !isUsableDocId(local[k]) && isUsableDocId(incoming[k])) {
        result[k] = incoming[k];
        continue;
      }
      if (k in local) {
        result[k] = local[k];
      } else if (k in incoming) {
        result[k] = incoming[k];
      }
      continue;
    }

    const baseValue = base == null ? undefined : base[k];
    const localValue = local[k];
    const incomingValue = incoming[k];
    const localChanged = !isSameMergeValue(localValue, baseValue);
    const incomingChanged = !isSameMergeValue(incomingValue, baseValue);

    // if this device never touched the field, the arriving version owns it —
    // including when the arriving version deleted it
    if (!localChanged) {
      if (incomingValue !== undefined) {
        result[k] = cloneIfObject(incomingValue);
      }
      continue;
    }

    // if the arriving version never touched the field, this device's change stands.
    // this is the branch that saves the data #4577 was losing
    if (!incomingChanged) {
      tally.localOnlyChangeCount++;
      if (localValue !== undefined) {
        result[k] = cloneIfObject(localValue);
      }
      continue;
    }

    // both sides moved away from the starting point but landed on the SAME
    // value, so there is nothing to disagree about. this must be settled before
    // anything is counted: counting it would report two devices as having
    // changed one field to different values when they agree, and that count is
    // what decides whether this is reported as a problem somebody should look at
    if (isSameMergeValue(localValue, incomingValue)) {
      if (localValue !== undefined) {
        result[k] = cloneIfObject(localValue);
      }
      continue;
    }

    // both sides changed this field. if both are objects, walk in so the
    // disagreement is narrowed to the fields that really clash rather than
    // being settled for the whole subtree at once
    if (isWalkableObject(localValue) && isWalkableObject(incomingValue)) {
      result[k] = mergeAgainstCommonBaseInner(
        isWalkableObject(baseValue) ? baseValue : null,
        localValue,
        incomingValue,
        preferIncomingOnTie,
        tally
      );
      continue;
    }

    // one side DELETED the field while the other side changed it. keep the
    // surviving value — the merge prefers keeping data over losing it.
    //
    // this case is handled here rather than being left to the two-way merge
    // below, which would reach it as a null and call deepClone on whatever the
    // other side holds. deepClone fires a console.assert for a plain value, and a
    // stray console.assert fails the E2E run through the unexpected-browser-error
    // gate — the same trap cloneIfObject exists to avoid.
    if (localValue === undefined || incomingValue === undefined) {
      tally.guessedFieldCount++;
      result[k] = cloneIfObject(localValue === undefined ? incomingValue : localValue);
      continue;
    }

    // a real disagreement this merge cannot resolve from the starting point:
    // hand it to the existing two-way rules and count it, so the caller can report
    // that a guess was made
    tally.guessedFieldCount++;
    result[k] = mergeConflictingDocsInner(localValue, incomingValue, preferIncomingOnTie);
  }

  return result;
}
